﻿import React, { useState, useCallback } from 'react';
import { getAuth } from 'firebase/auth';

// ─────────────────────────────────────────────────────────────────────────────
// Math helpers (two-proportion z-test + sample-size estimation)
// ─────────────────────────────────────────────────────────────────────────────
function normalCDF(x) {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x) / Math.SQRT2;
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1.0 + sign * y);
}

function twoProportionZConfidence(cA, nA, pooledCB, pooledNB) {
  if (nA < 5 || pooledNB < 5) return 0;
  const p1 = cA / nA, p2 = pooledCB / pooledNB;
  const p = (cA + pooledCB) / (nA + pooledNB);
  const denom = Math.sqrt(p * (1 - p) * (1 / nA + 1 / pooledNB));
  if (denom === 0) return 0;
  const z = Math.abs((p1 - p2) / denom);
  return (normalCDF(z) - 0.5) * 2; // two-tailed confidence as 0-1
}

function requiredN(baseCvr) {
  if (!baseCvr || baseCvr <= 0 || baseCvr >= 1) return 500;
  const p2 = baseCvr * 1.2;
  if (p2 >= 1) return 500;
  const za = 1.9600, zb = 0.8416;
  return Math.max(Math.ceil(
    Math.pow(za + zb, 2) * (baseCvr * (1 - baseCvr) + p2 * (1 - p2)) /
    Math.pow(p2 - baseCvr, 2)
  ), 50);
}

// ─────────────────────────────────────────────────────────────────────────────
// Status colours
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_STYLE = {
  queued:  { dot: 'bg-slate-500', badge: 'bg-slate-500/10 text-slate-400 border-slate-500/20' },
  active:  { dot: 'bg-blue-500 animate-pulse', badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  killed:  { dot: 'bg-red-500', badge: 'bg-red-500/10 text-red-400 border-red-500/20' },
  scaling: { dot: 'bg-emerald-500 animate-pulse', badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
};

const VERDICT_STYLE = {
  COLLECTING: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  KILL:       'text-red-400 bg-red-500/10 border-red-500/20',
  SCALE:      'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  KEEP:       'text-amber-400 bg-amber-500/10 border-amber-500/20',
};

// ─────────────────────────────────────────────────────────────────────────────
// Per-angle derived stats
// ─────────────────────────────────────────────────────────────────────────────
function deriveAngleStats(angle, allAngles, breakEvenCpc) {
  const clicks = (angle.networkReportedClicks > 0 ? angle.networkReportedClicks : angle.uniqueVisits) || 0;
  const clicksSource = angle.networkReportedClicks > 0 ? 'network' : 'tracked';
  const visits = angle.visits || 0;
  const spend = parseFloat(angle.spend) || 0;
  const optins = angle.optins || 0;
  const sales = angle.sales || 0;
  const revenue = parseFloat(angle.revenue) || 0;

  const cpc = clicks > 0 ? spend / clicks : 0;
  const cvr = clicks > 0 ? sales / clicks : 0;
  const epc = clicks > 0 ? revenue / clicks : 0;
  const roi = spend > 0 ? ((revenue - spend) / spend) * 100 : 0;

  // Pool all other angles for z-test baseline
  const otherSales = allAngles.filter((_, i) => i !== allAngles.indexOf(angle)).reduce((s, a) => s + (a.sales || 0), 0);
  const otherClicks = allAngles.filter((_, i) => i !== allAngles.indexOf(angle)).reduce((s, a) => {
    const c = a.networkReportedClicks > 0 ? a.networkReportedClicks : (a.uniqueVisits || 0);
    return s + c;
  }, 0);
  const confidence = twoProportionZConfidence(sales, clicks || 1, otherSales, otherClicks || 1);

  // Pooled base CVR across all angles for sample-size calc
  const totalSales = allAngles.reduce((s, a) => s + (a.sales || 0), 0);
  const totalClicks = allAngles.reduce((s, a) => {
    const c = a.networkReportedClicks > 0 ? a.networkReportedClicks : (a.uniqueVisits || 0);
    return s + c;
  }, 0);
  const baseCvr = totalClicks > 0 ? totalSales / totalClicks : 0.02;
  const required = requiredN(baseCvr);

  // Tracking discrepancy
  const netClicks = angle.networkReportedClicks || 0;
  const trackedClicks = angle.uniqueVisits || 0;
  const hasDiscrepancy = netClicks > 0 && trackedClicks > 0 &&
    Math.abs(netClicks - trackedClicks) / netClicks > 0.25;

  // Verdict
  let verdict = 'COLLECTING';
  if (clicks >= required) {
    const bec = parseFloat(breakEvenCpc) || 0;
    if (confidence >= 0.95 && bec > 0 && epc < bec) verdict = 'KILL';
    else if (confidence >= 0.95 && bec > 0 && epc >= bec * 1.2) verdict = 'SCALE';
    else verdict = 'KEEP';
  }

  return { clicks, clicksSource, visits, spend, optins, sales, revenue, cpc, cvr, epc, roi, confidence, verdict, required, hasDiscrepancy };
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function AngleComparisonTable({ campaign, campaignId, breakEvenCpc, onAngleUpdated }) {
  const allAngles = campaign?.angles || campaign?.anglePlans || [];
  const [editingClicks, setEditingClicks] = useState({}); // angleIdx -> string
  const [saving, setSaving] = useState({});

  const getAuthHeaders = async () => {
    const token = await getAuth().currentUser?.getIdToken();
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  const saveAngle = useCallback(async (angleIdx, patch) => {
    setSaving(s => ({ ...s, [angleIdx]: true }));
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/campaigns/${campaignId}/angles/${angleIdx}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const data = await res.json();
        onAngleUpdated?.(angleIdx, data.angle);
      }
    } catch (err) {
      console.error('[ANGLE SAVE ERROR]', err);
    } finally {
      setSaving(s => ({ ...s, [angleIdx]: false }));
    }
  }, [campaignId, onAngleUpdated]);

  if (!allAngles.length) {
    return (
      <div className="text-center py-8 text-slate-500 text-xs font-bold">
        No angles defined. Use the Campaign Wizard to add angles.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] font-mono">
        <thead>
          <tr className="border-b border-white/[0.08]">
            {['Angle','Status','Spend','Clicks*','CPC','Optins','CVR','EPC','ROI','Conf.','Verdict'].map(h => (
              <th key={h} className="px-3 py-2 text-left text-[9px] font-black uppercase tracking-widest text-slate-500 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allAngles.map((angle, idx) => {
            const stats = deriveAngleStats(angle, allAngles, breakEvenCpc);
            const st = STATUS_STYLE[angle.status] || STATUS_STYLE.queued;
            const vStyle = VERDICT_STYLE[stats.verdict] || VERDICT_STYLE.COLLECTING;
            const isSaving = saving[idx];

            return (
              <tr key={angle.id || idx} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                {/* Angle Name */}
                <td className="px-3 py-3 text-white font-bold max-w-[140px]">
                  <div className="truncate" title={angle.name || `Angle ${idx + 1}`}>
                    {angle.name || `Angle ${idx + 1}`}
                  </div>
                  {angle.pageSlug && (
                    <div className="text-[9px] text-slate-600 font-mono truncate">{angle.pageSlug}</div>
                  )}
                  {angle.adDestinationUrl && (
                    <button
                      onClick={() => navigator.clipboard.writeText(angle.adDestinationUrl)}
                      className="text-[8px] text-blue-400 hover:text-blue-300 transition-colors mt-0.5 block"
                      title="Copy Ad Link"
                    >
                      📋 Copy Ad Link
                    </button>
                  )}
                </td>

                {/* Status dropdown */}
                <td className="px-3 py-3">
                  <select
                    value={angle.status || 'queued'}
                    onChange={e => saveAngle(idx, { status: e.target.value })}
                    disabled={isSaving}
                    className={`text-[9px] font-black px-2 py-1 rounded border cursor-pointer bg-transparent focus:outline-none ${st.badge}`}
                  >
                    {['queued','active','killed','scaling'].map(s => (
                      <option key={s} value={s} className="bg-slate-900 text-white">{s.toUpperCase()}</option>
                    ))}
                  </select>
                </td>

                {/* Spend */}
                <td className="px-3 py-3 text-slate-300 font-mono">
                  ${stats.spend.toFixed(2)}
                </td>

                {/* Clicks* with inline networkReportedClicks input */}
                <td className="px-3 py-3">
                  <div className="flex flex-col gap-1">
                    <span className={`font-mono font-bold ${stats.clicksSource === 'network' ? 'text-white' : 'text-slate-400'}`}>
                      {stats.clicks.toLocaleString()}
                    </span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        placeholder="Net."
                        value={editingClicks[idx] ?? (angle.networkReportedClicks || '')}
                        onChange={e => setEditingClicks(s => ({ ...s, [idx]: e.target.value }))}
                        onBlur={e => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val) && val !== angle.networkReportedClicks) {
                            saveAngle(idx, { networkReportedClicks: val });
                          }
                        }}
                        className="w-14 bg-slate-800/50 border border-white/[0.08] rounded px-1 py-0.5 text-[9px] text-white focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    {stats.hasDiscrepancy && (
                      <span className="text-[8px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded font-black whitespace-nowrap">
                        ⚠ Discrepancy
                      </span>
                    )}
                  </div>
                </td>

                {/* CPC */}
                <td className="px-3 py-3 font-mono text-slate-300">
                  ${stats.cpc.toFixed(3)}
                </td>

                {/* Optins */}
                <td className="px-3 py-3 font-mono text-slate-300">
                  {stats.optins.toLocaleString()}
                </td>

                {/* CVR */}
                <td className="px-3 py-3 font-mono">
                  <span className={stats.cvr > 0 ? 'text-emerald-400' : 'text-slate-500'}>
                    {(stats.cvr * 100).toFixed(2)}%
                  </span>
                </td>

                {/* EPC */}
                <td className="px-3 py-3 font-mono">
                  <span className={stats.epc > parseFloat(breakEvenCpc || 0) ? 'text-emerald-400' : 'text-red-400'}>
                    ${stats.epc.toFixed(3)}
                  </span>
                </td>

                {/* ROI */}
                <td className="px-3 py-3 font-mono">
                  <span className={stats.roi > 0 ? 'text-emerald-400' : stats.roi < 0 ? 'text-red-400' : 'text-slate-500'}>
                    {stats.roi.toFixed(1)}%
                  </span>
                </td>

                {/* Confidence */}
                <td className="px-3 py-3 font-mono">
                  <span className={stats.confidence >= 0.95 ? 'text-emerald-400' : 'text-slate-400'}>
                    {(stats.confidence * 100).toFixed(0)}%
                  </span>
                  <div className="text-[8px] text-slate-600">
                    n={stats.required}
                  </div>
                </td>

                {/* Verdict */}
                <td className="px-3 py-3">
                  <span className={`text-[9px] font-black px-2 py-1 rounded border whitespace-nowrap ${vStyle}`}>
                    {stats.verdict}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-[9px] text-slate-600 mt-3 px-3">
        * Clicks: uses Network Reported Clicks when entered; falls back to tracked unique visits. Enter network clicks in the "Net." field per row.
      </p>
    </div>
  );
}
